import { useEffect } from 'react';

import { Building2Icon, PlusIcon } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { useDispatch, useSelector } from 'react-redux';
import { useTitle } from 'react-use';

import ContextualHelp from '@/components/atoms/ContextualHelp/ContextualHelp';
import { MODAL_DEPARTMENT_CREATE } from '@/components/organisms/Modals/ModalConductor';
import PageContentWrapper from '@/components/templates/PageContentWrapper';
import { SettingsContent } from '@/components/templates/SettingsContent/SettingsContent';
import { Button } from '@/components/ui/button';
import {
  Empty,
  EmptyContent,
  EmptyDescription,
  EmptyHeader,
  EmptyMedia,
  EmptyTitle,
} from '@/components/ui/empty';
import { createInitialState } from '@/hooks/useAccordionState/helpers';
import { useAccordionState } from '@/hooks/useAccordionState/useAccordionState';
import { useDeskId } from '@/hooks/useDeskId';
import useDesks from '@/hooks/useDesks';
import { pushModal } from '@/redux/modals/actions';
import { getPermissions } from '@/redux/permissions/selectors';
import useSegments from '@/segment/useSegments';
import { actions } from '@/shared/types/permissions';
import { PageName } from '@/shared/types/segment';
import { RootState } from '@/shared/types/state';

import DepartmentTile from '../components/DepartmentTile/DepartmentTile';
import { useDepartments } from '../hooks/useDepartments';

const Departments = () => {
  useSegments(PageName.DEPARTMENTS);
  const deskId = useDeskId();
  const { departments, listStatus } = useDepartments(deskId);
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const canWrite = useSelector((state: RootState) =>
    getPermissions(state, 'departments', actions.WRITE)
  );
  const handleClick = () => {
    dispatch(pushModal(MODAL_DEPARTMENT_CREATE));
  };
  const { desks } = useDesks();
  const desk = desks?.find((desk) => desk.desk_id === deskId);
  useTitle(t('pages.departments', { 0: desk?.name ?? '-' }));

  const { state, setExpanded, setInitState, setOnSubmit, setDirty, setTriggerValidation } =
    useAccordionState({});

  useEffect(() => {
    if (departments) {
      const newState = createInitialState(departments.map((d) => d.department_id));
      setInitState(newState);
    }
  }, [departments, setInitState]);

  const isLoading = listStatus === 'pending';
  return (
    <PageContentWrapper>
      <SettingsContent className="max-w-(--global-container-width)">
        <ContextualHelp title={t('departments.help_title')} name="departments-help" size="full">
          {t('departments.help_body')}
        </ContextualHelp>

        {departments?.length === 0 && !isLoading && (
          <Empty>
            <EmptyHeader>
              <EmptyMedia variant="icon">
                <Building2Icon aria-hidden />
              </EmptyMedia>
              <EmptyTitle>{t('departments.add_department')}</EmptyTitle>
              <EmptyDescription>{t('departments.empty_description')}</EmptyDescription>
            </EmptyHeader>
            {canWrite && (
              <EmptyContent>
                <Button size="sm" onClick={handleClick}>
                  <PlusIcon size={16} />
                  {t('common.create')}
                </Button>
              </EmptyContent>
            )}
          </Empty>
        )}
        <div className="mx-auto max-w-xl">
          {Object.keys(state).length > 0 &&
            departments?.map((department) => {
              const panelId = department.department_id;

              return (
                <DepartmentTile
                  setTriggerValidation={setTriggerValidation}
                  key={department.department_id}
                  department={department}
                  isExpanded={state[panelId]?.isExpanded || false}
                  panelId={panelId}
                  setExpanded={setExpanded(panelId)}
                  ref={state[panelId]?.ref}
                  setOnSubmit={setOnSubmit}
                  setDirty={setDirty}
                />
              );
            })}
        </div>
      </SettingsContent>
    </PageContentWrapper>
  );
};

export default Departments;
